import { Checkbox, Form, FormInstance, Row, Select } from "antd";
import { useFetchImpoundTypes } from "./hooks/useFetchImpoundTypes";
import { FormButtons } from "../../../../../../../../../../components/DefaultFormButtons";
import { useButtonHandlers } from "../../hooks/useButtonHandlers";
import { FloatNumberInput } from "../../../../../../../../../../components/FloatNumberInput";
import { useMemo } from "react";
import { Rule } from "antd/es/form";
import { CustomerInputSearch } from "../../../../../../../../../../components/CustomerInputSearch";
import { ToggleUpcNumbers } from "./ToggleUpcNumbers";
import { IMPOUND_BILL_AMOUNT_MIN } from "../../../../../../../../../../lib/constants";
import { transformToFloat } from "../../../../../../../../../../lib/transformToFloat";

type ImpoundFormProps = {
  impoundAccount: any | null;
  reloadData: () => void;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setId: React.Dispatch<React.SetStateAction<string | null>>;
  parentId?: string;
};

export const ImpoundForm = ({ impoundAccount, reloadData, setIsOpen, setId, parentId }: ImpoundFormProps) => {
  const [form]: FormInstance[] = Form.useForm();

  const { onCancelBtnClick, onDeleteBtnClick, onSaveBtnClick, isDisabled } = useButtonHandlers({
    reloadData,
    setIsOpen,
    setId,
    form,
    parentId
  });

  const { types, isTypesLoading } = useFetchImpoundTypes();

  const rules: Rule[] = useMemo(() => [{ required: true, validateTrigger: "onSubmit" }], []);

  const billAmountRules = useMemo(
    () => [
      {
        required: true,
        validateTrigger: "onSubmit"
      },
      () => ({
        validateTrigger: "onSubmit",
        transform: transformToFloat,
        validator(_: Rule, value: number | null) {
          if (value === null || value === undefined) {
            return Promise.resolve();
          }

          const balance = transformToFloat(form.getFieldValue("balance")) ?? 0;

          if (value > balance) {
            return Promise.resolve();
          }

          return Promise.reject(new Error(IMPOUND_BILL_AMOUNT_MIN));
        }
      })
    ],
    [form]
  );

  const shortageEnabled = Form.useWatch("showShortageForUsers", form);

  const showBillAmount: boolean = useMemo(() => {
    return shortageEnabled === true;
  }, [shortageEnabled]);

  return (
    <Row className="impound-form w-100">
      <Form
        form={form}
        disabled={isDisabled}
        className="pa-6"
        layout="vertical"
        requiredMark={false}
        autoComplete="off"
        initialValues={impoundAccount}
      >
        <FloatNumberInput
          name="balance"
          label="Current Impound Balance"
          form={form}
          includeThousandsSeparator
          className="current-balance mb-0"
          disabled
        />
        <Form.Item name="showShortageForUsers" className="mb-6 w-100" valuePropName="checked">
          <Checkbox>Show impound shortage to users</Checkbox>
        </Form.Item>
        {showBillAmount && (
          <FloatNumberInput
            name="billAmount"
            label="Bill Amount"
            form={form}
            includeThousandsSeparator
            rules={billAmountRules}
          />
        )}
        <FloatNumberInput
          name="impoundAccount"
          label="Impound Account"
          form={form}
          rules={rules}
          decimalLimit={0}
          prefix=""
          allowDecimal={false}
          className="current-balance"
        />
        <Form.Item label="Type" name="impoundTypeId" rules={rules}>
          <Select placeholder="Select Type" options={types} disabled={isTypesLoading || isDisabled} />
        </Form.Item>
        <FloatNumberInput
          name="amount"
          label="Payment Amount to Impound"
          form={form}
          includeThousandsSeparator
          rules={rules}
        />
        <CustomerInputSearch
          form={form}
          label="Payee (Impound Account)"
          name="payeeNumber"
          isFormDisabled={isDisabled}
          placeholder="Input Payee#, “Enter” to confirm"
          idItemName="payeeId"
        />
        <ToggleUpcNumbers form={form} disabled={isDisabled} />
      </Form>
      <FormButtons
        onCancelClick={onCancelBtnClick}
        onDeleteClick={onDeleteBtnClick}
        onSaveClick={onSaveBtnClick}
        isFormDisabled={isDisabled}
        currentId={impoundAccount?.id || null}
        controller="Impounds"
      />
    </Row>
  );
};
